import { Animated, Easing, View } from 'react-native';
import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../../utils/theme';
import { BaseTabViewProps, SideTabContentWidthVariant } from './utils';
import TabContentContainer from './TabContentContainer';
import Stack from '../Stack';
import Text from '../Text';
import MainContentContainer, { mainContentPadding } from '../MainContentContainer';
import { ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT } from '../ActionButtonLayout/ActionButtonLayout';
import Divider from '../Divider';
import SideTabButton from './SideTabButton';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import { ANIMATION_DURATION, SIDE_TAB_WIDTH_VARIANTS } from './constants';
import { IsInSideTabContentContainerContext } from './hooks';
import { PRScrollView } from '../../shared/Scroll';

export type SideTabViewViewOwnProps = BaseTabViewProps & {
    renderHeader?: () => React.ReactNode;
};
export type SideTabViewViewProps = SideTabViewViewOwnProps;

const SideTabViewView: React.FC<SideTabViewViewProps> = (props) => {
    const { forceUpdate } = useForceUpdate();
    const [tabContainerWidth, setTabContainerWidth] = useState<number>(0);
    const [displayAreaWidth, setDisplayAreaWidth] = useState<number | null>(null);

    const selectedTab = props.tabs.find((tab) => tab.value === props.selectedTab);
    useEffect(() => {
        forceUpdate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedTab]);
    const curContentWidth: SideTabContentWidthVariant = selectedTab?.sideTabContentWidthVariant || 'lg';
    const contentWidthAnimation = useRef<Animated.Value>(new Animated.Value(SIDE_TAB_WIDTH_VARIANTS[curContentWidth]));
    useEffect(() => {
        Animated.timing(contentWidthAnimation.current, {
            toValue: SIDE_TAB_WIDTH_VARIANTS[curContentWidth],
            duration: ANIMATION_DURATION,
            useNativeDriver: false,
            easing: Easing.inOut(Easing.ease),
        }).start();
    }, [curContentWidth]);

    return (
        <MainContentContainer style={{ width: '100%', height: '100%', flex: 1 }}>
            <View
                style={{
                    width: '100%',
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                }}
            >
                <Stack vertical style={{ marginRight: mainContentPadding, maxHeight: '100%' }}>
                    <PRScrollView
                        testID="side-tab-scrollview"
                        contentContainerStyle={{ backgroundColor: colors.White }}
                    >
                        <Stack fullWidth vertical padding={2} style={{ borderRadius: 8, padding: 8 }}>
                            {props.renderHeader ? props.renderHeader() : null}
                            {props.tabs
                                .filter((_) => !_.hide)
                                .map((tab) => (
                                    <SideTabButton
                                        key={tab.value}
                                        isDisabled={!!tab.isDisabled}
                                        isSelected={tab.value === props.selectedTab}
                                        onClick={() => props.onChange(tab.value)}
                                        title={tab.title}
                                        value={tab.value}
                                        secondaryText={tab.secondaryText}
                                        iconName={tab.icon}
                                        inset={tab.inset}
                                        heavy={tab.heavy}
                                    />
                                ))}
                        </Stack>
                    </PRScrollView>
                </Stack>
                <View
                    style={{ flex: 1, height: '100%', overflow: 'hidden' }}
                    onLayout={(e) => setDisplayAreaWidth(e.nativeEvent.layout.width)}
                >
                    <Animated.View
                        style={{
                            width: contentWidthAnimation.current,
                            height: '100%',
                            overflow: 'hidden',
                            maxWidth: '100%',
                        }}
                    >
                        <Stack
                            vertical
                            flex={1}
                            fullHeight
                            onLayout={(e) => setTabContainerWidth(e.nativeEvent.layout.width)}
                            style={{
                                borderRadius: 8,
                                backgroundColor: colors.White,
                                overflow: 'hidden',
                            }}
                            align="flex-start"
                            testID="side-tab-content"
                        >
                            <View
                                style={{
                                    width: SIDE_TAB_WIDTH_VARIANTS[curContentWidth],
                                    height: '100%',
                                    overflow: 'hidden',
                                    maxWidth: displayAreaWidth || undefined,
                                }}
                            >
                                {selectedTab?.sideTabTitle ? (
                                    <>
                                        <Stack
                                            horizontal
                                            fullWidth
                                            style={{
                                                height: ACTION_BUTTON_LAYOUT_ACTION_BAR_HEIGHT,
                                                backgroundColor: colors.White,
                                            }}
                                            align="center"
                                            justify="flex-start"
                                        >
                                            <MainContentContainer>
                                                <Text variant="h4">{selectedTab.sideTabTitle}</Text>
                                            </MainContentContainer>
                                        </Stack>
                                        <Divider horizontal />
                                    </>
                                ) : null}
                                <IsInSideTabContentContainerContext.Provider value={true}>
                                    <TabContentContainer
                                        tabs={props.tabs}
                                        selectedTab={props.selectedTab}
                                        scrollable={!!props.scrollable}
                                        tabContainerWidth={tabContainerWidth}
                                        onlyRenderActiveTab={props.onlyRenderActiveTab}
                                    />
                                </IsInSideTabContentContainerContext.Provider>
                            </View>
                        </Stack>
                    </Animated.View>
                </View>
            </View>
        </MainContentContainer>
    );
};

export default React.memo(SideTabViewView);
